import { useState, type ReactNode } from 'react';
import { AlertDialog as A } from 'radix-ui';
import { WarningIcon } from '@phosphor-icons/react';
import { cn } from '@/lib/cn';
import { Button } from './Button';
import { overlayCls } from './Dialog';

export interface ConfirmDialogProps {
  open?: boolean;
  onOpenChange?: (o: boolean) => void;
  trigger?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  confirmLabel: ReactNode;
  cancelLabel?: ReactNode;
  tone?: 'default' | 'danger';
  /** May return a promise; the dialog shows a spinner and closes when it resolves. */
  onConfirm: () => void | Promise<void>;
  children?: ReactNode;
}

export function ConfirmDialog({
  open,
  onOpenChange,
  trigger,
  title,
  description,
  confirmLabel,
  cancelLabel = 'Ακύρωση',
  tone = 'default',
  onConfirm,
  children,
}: ConfirmDialogProps) {
  const [inner, setInner] = useState(false);
  const [busy, setBusy] = useState(false);
  const isOpen = open ?? inner;
  const setOpen = (o: boolean) => (onOpenChange ? onOpenChange(o) : setInner(o));
  return (
    <A.Root open={isOpen} onOpenChange={(o) => !busy && setOpen(o)}>
      {trigger && <A.Trigger asChild>{trigger}</A.Trigger>}
      <A.Portal>
        <A.Overlay className={overlayCls} />
        <A.Content className="fixed left-1/2 top-[20vh] z-50 w-[calc(100vw-2rem)] max-w-md -translate-x-1/2 rounded-lg border border-hairline bg-surface p-6 shadow-lg data-[state=open]:animate-[gh-dialog_220ms_var(--gh-ease-out)]">
          <div className="flex gap-4">
            {tone === 'danger' && (
              <span className="mt-0.5 inline-flex size-9 shrink-0 items-center justify-center rounded-full bg-negative-soft text-negative-ink">
                <WarningIcon weight="duotone" className="size-5" />
              </span>
            )}
            <div className="grid gap-1.5">
              <A.Title className="font-display-tight text-lg">{title}</A.Title>
              <A.Description
                className={cn('text-[0.875rem] text-ink-muted', !description && 'sr-only')}
              >
                {description ?? ''}
              </A.Description>
              {children}
            </div>
          </div>
          <div className="mt-6 flex justify-end gap-2">
            <A.Cancel asChild>
              <Button variant="secondary" disabled={busy}>
                {cancelLabel}
              </Button>
            </A.Cancel>
            <Button
              variant={tone === 'danger' ? 'danger' : 'primary'}
              loading={busy}
              onClick={async () => {
                setBusy(true);
                try {
                  await onConfirm();
                  setOpen(false);
                } finally {
                  setBusy(false);
                }
              }}
            >
              {confirmLabel}
            </Button>
          </div>
        </A.Content>
      </A.Portal>
    </A.Root>
  );
}
